import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/site/Logo";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import usePageMeta from "@/hooks/usePageMeta";

export const homeFor = (u) => ({ admin: "/admin", employe: "/equipe" }[u?.role] || "/espace-client");

const GoogleButton = () => {
  const go = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/espace-client";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button type="button" onClick={go} data-testid="google-login-btn"
      className="flex h-12 w-full items-center justify-center gap-3 rounded-md border border-[#0F2840]/20 bg-white text-sm font-semibold transition-colors hover:border-[#0F2840]">
      <svg viewBox="0 0 48 48" className="h-5 w-5"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
      Continuer avec Google
    </button>
  );
};

export default function Login() {
  usePageMeta("Espace client sécurisé");
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) nav(homeFor(user), { replace: true });
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      nav(homeFor(u), { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen md:grid-cols-2">
      <div className="relative hidden overflow-hidden bg-[#0F2840] md:block grain">
        <img src="/images/hero.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="relative flex h-full flex-col justify-between p-12">
          <Link to="/" data-testid="login-home-link"><img src="/logo.png" alt="Logo Conciergerie Familly 29" className="h-36 w-36 rounded-full bg-white shadow-2xl" /></Link>
          <div>
            <h1 className="font-display text-5xl font-semibold leading-tight text-white">Votre logement, suivi en toute transparence.</h1>
            <p className="mt-4 max-w-md text-white/75">Rendez-vous, rapports photo, devis, factures et paiement en ligne : tout est réuni dans votre espace.</p>
          </div>
          <div className="h-3 stripe-mariniere opacity-70" />
        </div>
      </div>
      <div className="flex items-center justify-center bg-white px-6 py-16">
        <div className="w-full max-w-sm">
          <Link to="/" className="md:hidden"><Logo /></Link>
          <p className="eyebrow mt-10 md:mt-0">Espace sécurisé</p>
          <h2 className="mt-2 font-display text-3xl font-semibold">Connexion</h2>
          <p className="mt-2 text-sm text-slate-600">Clients et administrateur. Les accès clients sont créés par la conciergerie.</p>
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12" data-testid="login-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Mot de passe</Label>
              <Input id="password" type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-12" data-testid="login-password-input" />
            </div>
            <button type="submit" disabled={loading} data-testid="login-submit-btn"
              className="flex h-12 w-full items-center justify-center gap-2 rounded-md bg-[#0F2840] text-sm font-bold text-white transition-colors hover:bg-[#1a3a5c] disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} Se connecter
            </button>
          </form>
          <p className="mt-3 text-right text-xs"><Link to="/mot-de-passe-oublie" className="font-semibold text-[#0F2840] underline" data-testid="login-forgot-link">Mot de passe oublié ou première connexion ?</Link></p>
          <div className="my-6 flex items-center gap-3 text-xs text-slate-400"><span className="h-px flex-1 bg-slate-200" />ou<span className="h-px flex-1 bg-slate-200" /></div>
          <GoogleButton />
          <p className="mt-3 text-xs text-slate-500" data-testid="google-login-help">Utilisez le compte Google associé à l'adresse enregistrée par la conciergerie. Aucun nouveau compte client n'est créé automatiquement.</p>
          <p className="mt-8 text-xs text-slate-500">Pas encore client ? <Link to="/contact" className="font-semibold text-[#D82C2C] hover:underline">Contactez-nous</Link> pour obtenir vos accès.</p>
        </div>
      </div>
    </div>
  );
}
